import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FaPlus, FaEdit, FaTrash, FaSync, FaExclamationTriangle,
  FaCheckCircle, FaShoppingCart, FaFileInvoiceDollar, FaWrench,
  FaChevronDown, FaChevronRight
} from 'react-icons/fa';
import { Spinner, Alert, Form, Button, Modal } from 'react-bootstrap';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import api from '../services/api';
import { defaultSelectStyles } from '../utils/selectStyles';

/* ─── helpers ──────────────────────────────────────────────────────────────── */
const fmt  = v => Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const fmtN = v => Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const calcEtatFront = (stock, seuilAlerte, seuilCritique) => {
  const s = parseFloat(stock) || 0;
  const sc = parseFloat(seuilCritique) || 0;
  const sa = parseFloat(seuilAlerte) || 0;
  if (sc > 0 && s <= sc) return 'critique';
  if (sa > 0 && s <= sa) return 'alerte';
  return 'ok';
};

/* ─── couleurs état ─────────────────────────────────────────────────────────── */
const ETAT_CFG = {
  critique: { color: '#c0392b', bg: '#fdf0ee', label: 'CRITIQUE', icon: <FaExclamationTriangle /> },
  alerte:   { color: '#e67e22', bg: '#fef9ee', label: 'ALERTE',   icon: <FaExclamationTriangle /> },
  ok:       { color: '#27ae60', bg: '#edfaf1', label: 'OK',       icon: <FaCheckCircle /> }
};

/* ─── jauge stock ───────────────────────────────────────────────────────────── */
function StockGauge({ stockActuel, seuilAlerte, seuilCritique, unite }) {
  const sa = parseFloat(seuilAlerte)  || 0;
  const sc = parseFloat(seuilCritique) || 0;
  const s  = parseFloat(stockActuel)   || 0;
  const max = Math.max(s * 1.3, sa * 1.5, sc * 1.5, 1);

  const pct = v => Math.min(100, Math.max(0, (v / max) * 100));

  const barColor = s <= sc && sc > 0 ? '#c0392b'
                 : s <= sa && sa > 0 ? '#e67e22'
                 : '#27ae60';

  return (
    <div style={{ margin: '10px 0' }}>
      <div style={{
        position: 'relative', height: 22, borderRadius: 11,
        background: '#f0e8df', overflow: 'hidden'
      }}>
        <div style={{
          width: `${pct(s)}%`, height: '100%',
          background: barColor, borderRadius: 11,
          transition: 'width .4s'
        }} />
        {sc > 0 && (
          <div style={{
            position: 'absolute', top: 0, bottom: 0,
            left: `${pct(sc)}%`,
            borderLeft: '2px dashed #c0392b', opacity: .8
          }} title={`Seuil critique: ${fmt(sc)}`} />
        )}
        {sa > 0 && (
          <div style={{
            position: 'absolute', top: 0, bottom: 0,
            left: `${pct(sa)}%`,
            borderLeft: '2px dashed #e67e22', opacity: .8
          }} title={`Seuil alerte: ${fmt(sa)}`} />
        )}
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 4, fontSize: '0.74rem', color: '#888', flexWrap: 'wrap' }}>
        <span><b style={{ color: barColor }}>{fmt(s)}</b> {unite || ''} en stock</span>
        {sa > 0 && <span>Alerte : <b style={{ color: '#e67e22' }}>{fmt(sa)}</b></span>}
        {sc > 0 && <span>Critique : <b style={{ color: '#c0392b' }}>{fmt(sc)}</b></span>}
      </div>
    </div>
  );
}

/* ─── badge état ────────────────────────────────────────────────────────────── */
function EtatBadge({ etat }) {
  const cfg = ETAT_CFG[etat] || ETAT_CFG.ok;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 10px', borderRadius: 12,
      background: cfg.bg, color: cfg.color,
      fontWeight: 700, fontSize: '0.78rem', border: `1px solid ${cfg.color}`
    }}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

/* ─── styles ─────────────────────────────────────────────────────────────── */
const BROWN = '#6d4e36';
const cardStyle = {
  border: '1px solid #e8d5c4', borderRadius: 10,
  marginBottom: 16, overflow: 'hidden'
};
const sectionBtn = (active) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '4px 12px', borderRadius: 8, fontSize: '0.82rem',
  border: `1px solid ${active ? BROWN : '#ddd'}`,
  background: active ? BROWN : '#fff',
  color: active ? '#fff' : BROWN,
  cursor: 'pointer', fontWeight: 600, transition: 'all .2s'
});
const inputStyle = {
  border: '1px solid #ddd', borderRadius: 6, padding: '6px 10px',
  fontSize: '0.88rem', width: '100%', outline: 'none'
};

/* ═══════════════════════════════════════════════════════════════════════════ */
const isFullAccess = (role) => ['admin', 'achat'].includes(role);

export default function ArticlesSuivisPage({ user }) {
  const fullAccess = isFullAccess(user?.role);

  /* ── state ── */
  const [chantiers,      setChantiers]      = useState([]);
  const [responsables,   setResponsables]   = useState([]);
  const [responsableSel, setResponsableSel] = useState(null);
  const [chantierSel,    setChantierSel]    = useState([]);

  // relations pour le filtre responsable→chantier (admin/achat seulement)
  const relations = useMemo(() =>
    chantiers.map(c => ({ responsable_id: c.manager_id, chantier_id: c.value })),
  [chantiers]);

  const chantierOptions = useMemo(() => {
    if (!fullAccess || !responsableSel) return chantiers;
    return chantiers.filter(c =>
      relations.some(r =>
        String(r.responsable_id) === String(responsableSel.value) &&
        String(r.chantier_id)    === String(c.value)
      )
    );
  }, [responsableSel, chantiers, relations, fullAccess]);

  const onChangeResponsable = (v) => {
    setResponsableSel(v);
    if (v) {
      const siens = chantiers.filter(c =>
        relations.some(r =>
          String(r.responsable_id) === String(v.value) &&
          String(r.chantier_id)    === String(c.value)
        )
      );
      setChantierSel(siens);
    } else {
      setChantierSel([]);
    }
  };
  const [artFiltSel,     setArtFiltSel]     = useState(null);
  const [datDepuis,      setDateDepuis]     = useState('');
  const [filtreEtat,     setFiltreEtat]     = useState('all');
  const [items,        setItems]        = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [expanded,     setExpanded]     = useState({});  // { [id]: { bdc: bool, fac: bool } }

  /* modals */
  const [showAdd,       setShowAdd]      = useState(false);
  const [editItem,      setEditItem]     = useState(null);   // pour modifier seuils
  const [corrItem,      setCorrItem]     = useState(null);   // pour correction stock

  /* form add */
  const [artSel,       setArtSel]       = useState(null);
  const [addSeuils,    setAddSeuils]    = useState({ seuil_alerte: '', seuil_critique: '' });
  const [addLoading,   setAddLoading]   = useState(false);
  const [addError,     setAddError]     = useState('');

  /* form edit seuils */
  const [editSeuils,   setEditSeuils]   = useState({ seuil_alerte: '', seuil_critique: '' });

  /* form correction */
  const [corrVal,      setCorrVal]      = useState('');
  const [corrNote,     setCorrNote]     = useState('');

  /* ── chantiers + responsables au montage ── */
  useEffect(() => {
    api.get('/articles-suivis/chantiers')
      .then(r => {
        const list = (r.data.chantiers || []).map(c => ({
          value: c.id, label: c.libelle,
          manager_id: c.manager_id, manager_login: c.manager_login
        }));
        setChantiers(list);
        // Utilisateurs restreints : auto-sélectionner tous leurs chantiers
        if (!fullAccess) setChantierSel(list);
      })
      .catch(() => {});

    if (fullAccess) {
      api.get('/articles-suivis/responsables')
        .then(r => setResponsables((r.data.responsables || []).map(u => ({ value: u.id, label: u.login }))))
        .catch(() => {});
    }
  }, [fullAccess]);

  /* ── chargement liste ── */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (chantierSel.length > 0) chantierSel.forEach(c => params.append('chantier_id', c.value));
      if (artFiltSel)  params.set('article_id',  artFiltSel.value);
      if (datDepuis)   params.set('date_depuis', datDepuis);
      const { data } = await api.get(`/articles-suivis?${params}`);
      setItems(data.items || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [chantierSel, artFiltSel, datDepuis]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ── chargement async articles ── */
  const loadArticles = useCallback(async (inputValue) => {
    if (!inputValue || inputValue.trim().length < 2) return [];
    try {
      const { data } = await api.get(`/articles-suivis/search-articles?q=${encodeURIComponent(inputValue.trim())}`);
      return (data.articles || []).map(a => ({
        value: a.IDARTICLE,
        label: a.LIBELLE,
        unite: a.UNITE,
        code:  a.CODE_PRODUIT,
        raw:   a
      }));
    } catch {
      return [];
    }
  }, []);

  /* ── toggle sections ── */
  const toggleSection = (id, key) => {
    setExpanded(prev => ({
      ...prev,
      [id]: { ...prev[id], [key]: !prev[id]?.[key] }
    }));
  };

  /* ── ajouter ── */
  const handleAdd = async () => {
    if (!artSel || chantierSel.length !== 1) { setAddError('Sélectionnez un article et un seul projet.'); return; }
    setAddLoading(true); setAddError('');
    try {
      await api.post('/articles-suivis', {
        id_article:     artSel.value,
        id_chantier:    chantierSel[0].value,
        seuil_alerte:   addSeuils.seuil_alerte   || 0,
        seuil_critique: addSeuils.seuil_critique || 0
      });
      setShowAdd(false);
      setArtSel(null);
      setAddSeuils({ seuil_alerte: '', seuil_critique: '' });
      fetchData();
    } catch (e) {
      setAddError(e.response?.data?.message || 'Erreur');
    } finally {
      setAddLoading(false);
    }
  };

  /* ── modifier seuils ── */
  const handleEditSave = async () => {
    if (!editItem) return;
    try {
      await api.put(`/articles-suivis/${editItem.id}`, editSeuils);
      setEditItem(null);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.message || 'Erreur');
    }
  };

  /* ── correction stock ── */
  const handleCorrSave = async () => {
    if (!corrItem || corrVal === '') return;
    try {
      await api.put(`/articles-suivis/${corrItem.id}/correction`, {
        valeur: parseFloat(corrVal) || 0,
        note:   corrNote
      });
      setCorrItem(null); setCorrVal(''); setCorrNote('');
      fetchData();
    } catch (e) {
      alert(e.response?.data?.message || 'Erreur');
    }
  };

  /* ── supprimer ── */
  const handleDelete = async (item) => {
    if (!window.confirm(`Supprimer le suivi de "${item.designation}" ?`)) return;
    try {
      await api.delete(`/articles-suivis/${item.id}`);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.message || 'Erreur');
    }
  };

  /* ── filtrage client-side ── */
  const displayedItems = items.filter(i => {
    if (filtreEtat === 'critique') return i.etat === 'critique';
    if (filtreEtat === 'alerte')   return i.etat === 'alerte';
    if (filtreEtat === 'ok')       return i.etat === 'ok';
    if (filtreEtat === 'bdc')      return (i.bdc_pending?.length || 0) > 0;
    if (filtreEtat === 'fac')      return (i.factures_np?.length  || 0) > 0;
    return true;
  });

  /* ═══════════════════════════ RENDER ══════════════════════════════════════ */
  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px', fontFamily: 'sans-serif' }}>

      {/* ── En-tête ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <div>
          <h4 style={{ color: BROWN, fontWeight: 700, margin: 0 }}>Articles Critiques</h4>
          <p style={{ color: '#888', margin: 0, fontSize: '0.85rem' }}>Suivi stock, BDC en attente et factures à payer</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={fetchData}
            style={{ background: 'none', border: '1px solid #ddd', borderRadius: 6, padding: '6px 10px', cursor: loading ? 'default' : 'pointer', color: BROWN }}
            title="Rafraîchir"
            disabled={loading}
          >
            {loading
              ? <Spinner animation="border" size="sm" style={{ width: 14, height: 14, borderWidth: 2, color: BROWN }} />
              : <FaSync />}
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              disabled={chantierSel.length !== 1}
              onClick={() => { setArtSel(null); setAddError(''); setAddSeuils({ seuil_alerte: '', seuil_critique: '' }); setShowAdd(true); }}
              style={{
                background: chantierSel.length === 1 ? BROWN : '#c8b8ac',
                color: '#fff', border: 'none', borderRadius: 8,
                padding: '7px 16px', fontWeight: 600,
                cursor: chantierSel.length === 1 ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', gap: 6,
                opacity: chantierSel.length === 1 ? 1 : 0.7
              }}
            >
              <FaPlus /> Ajouter un article
            </button>
            {chantierSel.length !== 1 && (
              <span style={{ fontSize: '0.78rem', color: '#999', fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                {chantierSel.length === 0 ? 'Choisir un projet d\'abord' : 'Sélectionner un seul projet pour ajouter'}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Filtres ── */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20, alignItems: 'center' }}>
        {fullAccess && (
          <div style={{ minWidth: 180, flex: 1 }}>
            <Select
              options={responsables}
              value={responsableSel}
              onChange={onChangeResponsable}
              placeholder="Tous les responsables..."
              styles={defaultSelectStyles}
              isClearable
            />
          </div>
        )}
        <div style={{ minWidth: 200, flex: 1 }}>
          <Select
            options={chantierOptions}
            value={chantierSel}
            onChange={v => setChantierSel(v || [])}
            placeholder="Tous les projets..."
            styles={defaultSelectStyles}
            isMulti
            isClearable
            hideSelectedOptions={false}
            components={{
              MultiValue: ({ index, getValue, data, removeProps }) => {
                const selected = getValue();
                const allSelected = selected.length === chantierOptions.length && chantierOptions.length > 1;
                if (allSelected) {
                  if (index > 0) return null;
                  return (
                    <div style={{ padding: '2px 6px', fontSize: '0.85rem', color: BROWN, fontWeight: 600 }}>
                      Tous ({selected.length} projets)
                    </div>
                  );
                }
                // affichage normal avec tag + croix
                return (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    background: '#f0e8df', borderRadius: 4,
                    padding: '2px 6px', margin: '2px', fontSize: '0.82rem', color: BROWN
                  }}>
                    <span>{data.label}</span>
                    <span
                      onClick={removeProps.onClick}
                      style={{ cursor: 'pointer', color: '#aaa', fontWeight: 700, lineHeight: 1 }}
                    >×</span>
                  </div>
                );
              }
            }}
          />
        </div>
        <div style={{ minWidth: 220, flex: 1 }}>
          <AsyncSelect
            loadOptions={loadArticles}
            value={artFiltSel}
            onChange={setArtFiltSel}
            placeholder="Tous les articles..."
            noOptionsMessage={({ inputValue }) =>
              !inputValue || inputValue.length < 2 ? 'Taper au moins 2 caractères...' : 'Aucun article trouvé'
            }
            loadingMessage={() => 'Recherche...'}
            isClearable
            styles={defaultSelectStyles}
            formatOptionLabel={(opt) => (
              <div>
                <span style={{ color: '#aaa', fontSize: '0.8rem', marginRight: 6 }}>#{opt.value}</span>
                <span style={{ fontWeight: 600 }}>{opt.label}</span>
                {opt.unite && <span style={{ color: '#bbb', marginLeft: 6, fontSize: '0.8rem' }}>({opt.unite})</span>}
              </div>
            )}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <label style={{ fontSize: '0.72rem', color: '#aaa', marginBottom: 1 }}>BDC & factures depuis</label>
          <input
            type="date"
            value={datDepuis}
            onChange={e => setDateDepuis(e.target.value)}
            style={{ ...inputStyle, width: 160, fontSize: '0.83rem', padding: '5px 8px' }}
          />
        </div>
        {filtreEtat !== 'all' && (
          <button
            onClick={() => setFiltreEtat('all')}
            style={{
              padding: '6px 14px', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
              border: `1px solid #ddd`, background: '#fff', color: BROWN,
              whiteSpace: 'nowrap'
            }}
          >✕ Réinitialiser filtre</button>
        )}
      </div>

      {/* ── KPI cards ── */}
      {items.length > 0 && (() => {
        const nbCritique  = items.filter(i => i.etat === 'critique').length;
        const nbAlerte    = items.filter(i => i.etat === 'alerte').length;
        const nbOk        = items.filter(i => i.etat === 'ok').length;
        const nbBdcTotal  = items.reduce((s, i) => s + (i.bdc_pending?.length || 0), 0);
        const mtBdc       = items.reduce((s, i) => s + (i.bdc_pending || []).reduce((a, b) => a + (parseFloat(b.montant_restant_ttc) || 0), 0), 0);
        const nbFacTotal  = items.reduce((s, i) => s + (i.factures_np?.length || 0), 0);
        const mtFac       = items.reduce((s, i) => s + (i.factures_np || []).reduce((a, f) => a + (parseFloat(f.total_ttc_left) || 0), 0), 0);

        const kpis = [
          { key: 'all',      label: 'Articles suivis',   value: items.length, sub: null,                     color: '#6d4e36', bg: '#fdf6f0' },
          { key: 'critique', label: 'Critiques',          value: nbCritique,   sub: 'stock sous seuil rouge',  color: '#c0392b', bg: '#fdf0ee' },
          { key: 'alerte',   label: 'En alerte',          value: nbAlerte,     sub: 'stock sous seuil orange', color: '#e67e22', bg: '#fef9ee' },
          { key: 'ok',       label: 'OK',                 value: nbOk,         sub: 'stock suffisant',          color: '#27ae60', bg: '#edfaf1' },
          { key: 'bdc',      label: 'BDC en attente',     value: nbBdcTotal,   sub: fmtN(mtBdc) + ' MAD',      color: '#1565c0', bg: '#eef4fc' },
          { key: 'fac',      label: 'Factures à payer',  value: nbFacTotal,   sub: fmtN(mtFac) + ' MAD',      color: '#8e44ad', bg: '#f5eefb' },
        ];

        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 20 }}>
            {kpis.map((k) => {
              const active = filtreEtat === k.key;
              return (
                <div key={k.key} onClick={() => setFiltreEtat(k.key)} style={{
                  background: k.bg,
                  border: `1px solid ${active ? k.color : k.color + '22'}`,
                  borderRadius: 10, padding: '12px 16px',
                  borderLeft: `3px solid ${k.color}`,
                  cursor: 'pointer',
                  boxShadow: active ? `0 0 0 2px ${k.color}55` : 'none',
                  transform: active ? 'translateY(-1px)' : 'none',
                  transition: 'all .15s'
                }}>
                  <div style={{ fontSize: '1.6rem', fontWeight: 800, color: k.color, lineHeight: 1 }}>{k.value}</div>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, color: active ? k.color : '#555', marginTop: 4 }}>{k.label}</div>
                  {k.sub && <div style={{ fontSize: '0.72rem', color: '#999', marginTop: 2 }}>{k.sub}</div>}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* ── Erreur ── */}
      {!loading && error && (
        <Alert variant="danger">{error}</Alert>
      )}

      {!error && items.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: 60, color: '#bbb', border: '2px dashed #e8d5c4', borderRadius: 12 }}>
          <FaShoppingCart size={36} style={{ marginBottom: 12 }} />
          {!fullAccess && chantiers.length === 0 ? (
            <p>Aucun chantier associé à votre compte.<br />
              <span style={{ fontSize: '0.82rem' }}>Votre login doit correspondre au login manager dans le système de gestion.</span>
            </p>
          ) : (
            <p>Aucun article suivi{filtreEtat !== 'all' ? ' pour ce filtre' : ''}. <br />
              {filtreEtat === 'all' && chantierSel.length === 1 && <span>Cliquez sur <b>+ Ajouter un article</b> pour commencer.</span>}
            </p>
          )}
        </div>
      )}

      {/* ── Liste des articles suivis ── */}
      {displayedItems.map(item => {
        const etatCfg = ETAT_CFG[item.etat] || ETAT_CFG.ok;
        const bdcOpen = expanded[item.id]?.bdc;
        const facOpen = expanded[item.id]?.fac;
        const nbBdc   = item.bdc_pending?.length || 0;
        const nbFac   = item.factures_np?.length || 0;
        const totalBdcRestant = (item.bdc_pending || []).reduce((s, r) => s + (parseFloat(r.montant_restant_ttc) || 0), 0);
        const totalFacNp      = (item.factures_np  || []).reduce((s, r) => s + (parseFloat(r.total_ttc_left)     || 0), 0);

        return (
          <div key={item.id} style={{ ...cardStyle, borderLeft: `4px solid ${etatCfg.color}` }}>
            {/* ── Entête carte ── */}
            <div style={{ padding: '14px 18px', background: etatCfg.bg, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <div>
                {/* ligne 1 : chantier */}
                <div style={{ fontSize: '0.78rem', color: '#888', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {item.chantier_libelle}
                </div>
                {/* ligne 2 : article + code + badge */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, fontSize: '1.05rem', color: '#222' }}>
                    {item.article_libelle || item.designation}
                  </span>
                  {item.code_produit && <span style={{ fontSize: '0.78rem', color: '#999', background: '#f5f5f5', borderRadius: 4, padding: '1px 7px' }}>{item.code_produit}</span>}
                  <EtatBadge etat={item.etat} />
                </div>
                {/* ligne 3 : stock actuel mis en avant */}
                <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '1.1rem', fontWeight: 700, color: etatCfg.color }}>
                    {fmt(item.stock_actuel)} <span style={{ fontSize: '0.82rem', fontWeight: 400, color: '#888' }}>{item.article_unite || item.unite || ''} en stock</span>
                  </span>
                </div>
                {/* ligne 4 : dates */}
                <div style={{ marginTop: 6, display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '0.74rem', color: '#aaa' }}>
                  {item.derniere_mvt_auto && (
                    <span>
                      Dernier mouvement : <b style={{ color: '#777' }}>{fmtDate(item.derniere_mvt_auto)}</b>
                    </span>
                  )}
                  {item.derniere_correction && (
                    <span>
                      Correction : <b style={{ color: '#777' }}>{parseFloat(item.derniere_correction.valeur) >= 0 ? '+' : ''}{fmt(item.derniere_correction.valeur)}</b>
                      {' '}le <b style={{ color: '#777' }}>{fmtDate(item.derniere_correction.createdAt)}</b>
                      {item.derniere_correction.par && <> par <b style={{ color: '#777' }}>{item.derniere_correction.par}</b></>}
                      {item.corrections.length > 1 && <span style={{ color: '#bbb' }}> ({item.corrections.length} corrections)</span>}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={() => { setEditItem(item); setEditSeuils({ seuil_alerte: item.seuil_alerte, seuil_critique: item.seuil_critique }); }}
                  style={{ background: '#fff', border: `1px solid ${BROWN}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: BROWN, fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 4 }}
                  title="Modifier seuils"
                ><FaEdit /> Seuils</button>
                <button
                  onClick={() => { setCorrItem(item); setCorrVal(''); setCorrNote(''); }}
                  style={{ background: '#fff', border: `1px solid #e67e22`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: '#e67e22', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 4 }}
                  title="Corriger le stock"
                ><FaWrench /> Corriger</button>
                <button
                  onClick={() => handleDelete(item)}
                  style={{ background: '#fff', border: '1px solid #e74c3c', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#e74c3c', fontSize: '0.8rem' }}
                  title="Retirer du suivi"
                ><FaTrash /></button>
              </div>
            </div>

            {/* ── Jauge stock ── */}
            <div style={{ padding: '10px 18px 6px' }}>
              <StockGauge
                stockActuel={item.stock_actuel}
                seuilAlerte={item.seuil_alerte}
                seuilCritique={item.seuil_critique}
                unite={item.article_unite || item.unite}
              />
              <div style={{ fontSize: '0.72rem', color: '#bbb', marginTop: 2 }}>
                BR reçus : {fmt(item.stock_br)} &nbsp;|&nbsp;
                Transferts : +{fmt(item.transfers_in)} / -{fmt(item.transfers_out)} &nbsp;|&nbsp;
                Corrections ({item.corrections.length}) : {parseFloat(item.correction_totale) >= 0 ? '+' : ''}{fmt(item.correction_totale)}
              </div>
            </div>

            {/* ── Boutons sections ── */}
            <div style={{ padding: '8px 18px 14px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button style={sectionBtn(bdcOpen)} onClick={() => toggleSection(item.id, 'bdc')}>
                {bdcOpen ? <FaChevronDown size={11}/> : <FaChevronRight size={11}/>}
                <FaShoppingCart size={12} />
                {nbBdc} BDC en attente
                {totalBdcRestant > 0 && <span style={{ marginLeft: 4, fontWeight: 400 }}>({fmtN(totalBdcRestant)} MAD)</span>}
              </button>
              <button style={sectionBtn(facOpen)} onClick={() => toggleSection(item.id, 'fac')}>
                {facOpen ? <FaChevronDown size={11}/> : <FaChevronRight size={11}/>}
                <FaFileInvoiceDollar size={12} />
                {nbFac} Factures à payer
                {totalFacNp > 0 && <span style={{ marginLeft: 4, fontWeight: 400 }}>({fmtN(totalFacNp)} MAD)</span>}
              </button>
            </div>

            {/* ── BDC en attente ── */}
            {bdcOpen && (
              <div style={{ borderTop: '1px solid #f0e8df', padding: '0 18px 14px' }}>
                {nbBdc === 0 ? (
                  <p style={{ color: '#aaa', fontSize: '0.83rem', margin: '12px 0' }}>Aucun BDC signé avec réception en attente.</p>
                ) : (
                  <div style={{ overflowX: 'auto', marginTop: 12 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
                      <thead>
                        <tr style={{ background: '#faf5f0', color: BROWN }}>
                          <th style={th}>IDBDC</th>
                          <th style={th}>Date BDC</th>
                          <th style={th}>Fournisseur</th>
                          <th style={{ ...th, textAlign: 'right' }}>Qté commandée</th>
                          <th style={{ ...th, textAlign: 'right' }}>Qté restante</th>
                          <th style={{ ...th, textAlign: 'right' }}>Montant TTC</th>
                        </tr>
                      </thead>
                      <tbody>
                        {item.bdc_pending.map((b, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #f5efe8' }}>
                            <td style={td}>
                              <a href={`https://cienco.ngrok.io/fiche_bon_de_commande/${b.IDBDC}`} target="_blank" rel="noreferrer" style={{ color: BROWN, fontWeight: 600 }}>
                                #{b.IDBDC}
                              </a>
                            </td>
                            <td style={td}>{fmtDate(b.DATE_BDC)}</td>
                            <td style={td}>{b.fournisseur}</td>
                            <td style={{ ...td, textAlign: 'right' }}>{fmt(b.qte_commandee)} {b.unite}</td>
                            <td style={{ ...td, textAlign: 'right', color: '#e67e22', fontWeight: 600 }}>{fmt(b.qte_restante)} {b.unite}</td>
                            <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmtN(b.montant_restant_ttc)} MAD</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#faf5f0', fontWeight: 700 }}>
                          <td colSpan={5} style={{ ...td, textAlign: 'right', color: BROWN }}>Total restant :</td>
                          <td style={{ ...td, textAlign: 'right', color: BROWN }}>{fmtN(totalBdcRestant)} MAD</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ── Factures à payer ── */}
            {facOpen && (
              <div style={{ borderTop: '1px solid #f0e8df', padding: '0 18px 14px' }}>
                {nbFac === 0 ? (
                  <p style={{ color: '#aaa', fontSize: '0.83rem', margin: '12px 0' }}>Aucune facture à payer pour cet article.</p>
                ) : (
                  <div style={{ overflowX: 'auto', marginTop: 12 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
                      <thead>
                        <tr style={{ background: '#faf5f0', color: BROWN }}>
                          <th style={th}>N° Facture</th>
                          <th style={th}>Date</th>
                          <th style={th}>Fournisseur</th>
                          <th style={{ ...th, textAlign: 'right' }}>Total TTC</th>
                          <th style={{ ...th, textAlign: 'right' }}>Reste à payer</th>
                        </tr>
                      </thead>
                      <tbody>
                        {item.factures_np.map((f, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #f5efe8' }}>
                            <td style={td}>
                              <a href={`https://cienco.ngrok.io/fiche_facture/${f.IDFACTURE}`} target="_blank" rel="noreferrer" style={{ color: BROWN, fontWeight: 600 }}>
                                {f.NFacture || `#${f.IDFACTURE}`}
                              </a>
                            </td>
                            <td style={td}>{fmtDate(f.date_fac)}</td>
                            <td style={td}>{f.fournisseur}</td>
                            <td style={{ ...td, textAlign: 'right' }}>{fmtN(f.total_ttc)} MAD</td>
                            <td style={{ ...td, textAlign: 'right', color: '#c0392b', fontWeight: 600 }}>{fmtN(f.total_ttc_left)} MAD</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#faf5f0', fontWeight: 700 }}>
                          <td colSpan={4} style={{ ...td, textAlign: 'right', color: BROWN }}>Total à payer :</td>
                          <td style={{ ...td, textAlign: 'right', color: '#c0392b' }}>{fmtN(totalFacNp)} MAD</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* ═══ MODAL : Ajouter un article ═══ */}
      <Modal show={showAdd} onHide={() => setShowAdd(false)} centered>
        <Modal.Header closeButton style={{ borderColor: '#f0e8df' }}>
          <Modal.Title style={{ fontSize: '1rem', color: BROWN, fontWeight: 700 }}>
            Ajouter un article à suivre
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {chantierSel && (
            <p style={{ fontSize: '0.85rem', color: '#888', marginBottom: 12 }}>
              Chantier : <b style={{ color: BROWN }}>{chantierSel.label}</b>
            </p>
          )}
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Article</Form.Label>
            <AsyncSelect
              loadOptions={loadArticles}
              value={artSel}
              onChange={setArtSel}
              placeholder="Taper un nom ou code produit..."
              noOptionsMessage={({ inputValue }) =>
                !inputValue || inputValue.length < 2
                  ? 'Taper au moins 2 caractères...'
                  : 'Aucun article trouvé'
              }
              loadingMessage={() => 'Recherche...'}
              isClearable
              styles={{
                ...defaultSelectStyles,
                option: (base, state) => ({
                  ...base,
                  background: state.isFocused ? '#faf5f0' : '#fff',
                  color: '#333',
                  cursor: 'pointer'
                })
              }}
              formatOptionLabel={(opt) => (
                <div>
                  <span style={{ color: '#aaa', fontSize: '0.8rem', marginRight: 8 }}>#{opt.value}</span>
                  <span style={{ fontWeight: 600 }}>{opt.label}</span>
                  {opt.code && <span style={{ color: '#aaa', marginLeft: 8, fontSize: '0.82rem' }}>{opt.code}</span>}
                  {opt.unite && <span style={{ color: '#bbb', marginLeft: 8, fontSize: '0.8rem' }}>({opt.unite})</span>}
                </div>
              )}
            />
          </Form.Group>

          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Group className="mb-3" style={{ flex: 1 }}>
              <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Seuil alerte <span style={{ color: '#e67e22' }}>●</span></Form.Label>
              <input
                type="number" style={inputStyle} placeholder="0"
                value={addSeuils.seuil_alerte}
                onChange={e => setAddSeuils(p => ({ ...p, seuil_alerte: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3" style={{ flex: 1 }}>
              <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Seuil critique <span style={{ color: '#c0392b' }}>●</span></Form.Label>
              <input
                type="number" style={inputStyle} placeholder="0"
                value={addSeuils.seuil_critique}
                onChange={e => setAddSeuils(p => ({ ...p, seuil_critique: e.target.value }))}
              />
            </Form.Group>
          </div>
          {addError && <Alert variant="danger" style={{ fontSize: '0.85rem' }}>{addError}</Alert>}
        </Modal.Body>
        <Modal.Footer style={{ borderColor: '#f0e8df' }}>
          <Button variant="secondary" size="sm" onClick={() => setShowAdd(false)}>Annuler</Button>
          <Button
            size="sm"
            disabled={!artSel || addLoading}
            onClick={handleAdd}
            style={{ background: BROWN, border: 'none', fontWeight: 600 }}
          >
            {addLoading ? <Spinner size="sm" animation="border" /> : 'Ajouter'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ═══ MODAL : Modifier seuils ═══ */}
      <Modal show={!!editItem} onHide={() => setEditItem(null)} centered size="sm">
        <Modal.Header closeButton style={{ borderColor: '#f0e8df' }}>
          <Modal.Title style={{ fontSize: '1rem', color: BROWN, fontWeight: 700 }}>
            Modifier les seuils
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editItem && (
            <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: 14 }}>
              <b>{editItem.article_libelle || editItem.designation}</b>
            </p>
          )}
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Seuil alerte <span style={{ color: '#e67e22' }}>●</span></Form.Label>
            <input
              type="number" style={inputStyle}
              value={editSeuils.seuil_alerte}
              onChange={e => setEditSeuils(p => ({ ...p, seuil_alerte: e.target.value }))}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Seuil critique <span style={{ color: '#c0392b' }}>●</span></Form.Label>
            <input
              type="number" style={inputStyle}
              value={editSeuils.seuil_critique}
              onChange={e => setEditSeuils(p => ({ ...p, seuil_critique: e.target.value }))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ borderColor: '#f0e8df' }}>
          <Button variant="secondary" size="sm" onClick={() => setEditItem(null)}>Annuler</Button>
          <Button size="sm" onClick={handleEditSave} style={{ background: BROWN, border: 'none', fontWeight: 600 }}>
            Enregistrer
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ═══ MODAL : Correction stock ═══ */}
      <Modal show={!!corrItem} onHide={() => setCorrItem(null)} centered size="lg">
        <Modal.Header closeButton style={{ borderColor: '#f0e8df' }}>
          <Modal.Title style={{ fontSize: '1rem', color: '#e67e22', fontWeight: 700 }}>
            <FaWrench style={{ marginRight: 6 }} /> Corrections — {corrItem?.article_libelle || corrItem?.designation}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {corrItem && (() => {
            const unite      = corrItem.article_unite || corrItem.unite || '';
            const stockActuel = corrItem.stock_actuel || 0;
            const nouvDelta  = parseFloat(corrVal) || 0;
            const stockApres = stockActuel + nouvDelta;
            const etatApres  = calcEtatFront(stockApres, corrItem.seuil_alerte, corrItem.seuil_critique);
            const changed    = corrVal !== '';

            return (
              <>
                {/* ── Aperçu stock ── */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  <div style={{ flex: 1, background: '#f8f4f0', borderRadius: 8, padding: '10px 14px', border: '1px solid #e8d5c4' }}>
                    <div style={{ fontSize: '0.7rem', color: '#aaa', marginBottom: 4, textTransform: 'uppercase' }}>Stock actuel</div>
                    <div style={{ fontSize: '1.2rem', fontWeight: 800, color: BROWN }}>{fmt(stockActuel)} <span style={{ fontSize: '0.8rem', fontWeight: 400 }}>{unite}</span></div>
                  </div>
                  {changed && (
                    <div style={{ flex: 1, background: ETAT_CFG[etatApres]?.bg, borderRadius: 8, padding: '10px 14px', border: `1px solid ${ETAT_CFG[etatApres]?.color}44` }}>
                      <div style={{ fontSize: '0.7rem', color: '#aaa', marginBottom: 4, textTransform: 'uppercase' }}>Après cette correction</div>
                      <div style={{ fontSize: '1.2rem', fontWeight: 800, color: ETAT_CFG[etatApres]?.color }}>{fmt(stockApres)} <span style={{ fontSize: '0.8rem', fontWeight: 400 }}>{unite}</span></div>
                      <div style={{ fontSize: '0.72rem', fontWeight: 600, color: ETAT_CFG[etatApres]?.color }}>{ETAT_CFG[etatApres]?.icon} {ETAT_CFG[etatApres]?.label}</div>
                    </div>
                  )}
                </div>

                {/* ── Historique ── */}
                {corrItem.corrections.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: BROWN, marginBottom: 6 }}>Historique des corrections</div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                        <thead>
                          <tr style={{ background: '#faf5f0', color: BROWN }}>
                            <th style={th}>Date</th>
                            <th style={{ ...th, textAlign: 'right' }}>Delta</th>
                            <th style={th}>Par</th>
                            <th style={th}>Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...corrItem.corrections].reverse().map((c, i) => (
                            <tr key={c.id || i} style={{ borderBottom: '1px solid #f5efe8' }}>
                              <td style={td}>{fmtDate(c.createdAt)}</td>
                              <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: parseFloat(c.valeur) >= 0 ? '#27ae60' : '#c0392b' }}>
                                {parseFloat(c.valeur) >= 0 ? '+' : ''}{fmt(c.valeur)} {unite}
                              </td>
                              <td style={{ ...td, color: '#888' }}>{c.par || '—'}</td>
                              <td style={{ ...td, color: '#aaa', fontStyle: 'italic' }}>{c.note || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* ── Nouvelle correction ── */}
                <div style={{ borderTop: '1px solid #f0e8df', paddingTop: 14 }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 700, color: BROWN, marginBottom: 10 }}>Ajouter une correction</div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <Form.Group style={{ flex: '0 0 140px' }}>
                      <Form.Label style={{ fontSize: '0.82rem', fontWeight: 600 }}>Delta (+/-) {unite}</Form.Label>
                      <input
                        type="number" step="any" style={inputStyle}
                        placeholder="Ex: -5 ou +10"
                        value={corrVal}
                        onChange={e => setCorrVal(e.target.value)}
                        autoFocus
                      />
                    </Form.Group>
                    <Form.Group style={{ flex: 1 }}>
                      <Form.Label style={{ fontSize: '0.82rem', fontWeight: 600 }}>Note / motif</Form.Label>
                      <input
                        type="text" style={inputStyle}
                        placeholder="Ex: Inventaire physique du 06/04/2026"
                        value={corrNote}
                        onChange={e => setCorrNote(e.target.value)}
                      />
                    </Form.Group>
                  </div>
                </div>
              </>
            );
          })()}
        </Modal.Body>
        <Modal.Footer style={{ borderColor: '#f0e8df' }}>
          <Button variant="secondary" size="sm" onClick={() => setCorrItem(null)}>Fermer</Button>
          <Button size="sm" disabled={corrVal === ''} onClick={handleCorrSave} style={{ background: '#e67e22', border: 'none', fontWeight: 600 }}>
            Enregistrer la correction
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
}

/* ─── styles tableau ──────────────────────────────────────────────────────── */
const th = { padding: '7px 10px', fontWeight: 600, fontSize: '0.78rem', borderBottom: '2px solid #e8d5c4' };
const td = { padding: '6px 10px', color: '#444', verticalAlign: 'middle' };
